import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Input, 
  Button, 
  Table, 
  Space, 
  Tag, 
  Select,
  Checkbox,
  message,
  Row,
  Col,
  Descriptions,
  Spin,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  PlayCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { Video, TranscodeJobStatus } from './types';
import { formatDuration } from './utils';
import { makeKeyInLowerCaseHelper, LowerCaseKeyInterFace } from '../utils';

const { Search } = Input;
const { Option } = Select;

interface VideoSelectorProps {
  selectedVideos: Video[];
  onVideosSelected: (videos: Video[]) => void;
  distributionPlatformData?: any[];
}

const VideoSelector: React.FC<VideoSelectorProps> = ({ selectedVideos, onVideosSelected, distributionPlatformData = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [youtubeReadyFilter, setYoutubeReadyFilter] = useState<string>('all');
  const [distributionPlatformFilter, setDistributionPlatformFilter] = useState<string[]>([]);
  const [transcodeStatusMap, setTranscodeStatusMap] = useState<{ [key: string]: { status: TranscodeJobStatus; output_url?: string } }>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [metadataModalVisible, setMetadataModalVisible] = useState(false);
  const [selectedVideoForMetadata, setSelectedVideoForMetadata] = useState<Video | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);

  useEffect(() => {
    // Default to "YouTube Nosey" distribution platform
    if (distributionPlatformData && distributionPlatformData.length > 0) {
      const youtubeNosey = distributionPlatformData.find(
        (platform: any) => platform.name?.toLowerCase() === 'youtube nosey'
      );
      if (youtubeNosey) {
        setDistributionPlatformFilter([youtubeNosey.id]);
      }
    }
  }, [distributionPlatformData]);

  useEffect(() => {
    // Only search after distribution platform filter is initialized
    if (distributionPlatformFilter.length > 0 || distributionPlatformData.length === 0) {
      searchVideos();
    }
  }, [distributionPlatformFilter]);

  // Sync selectedRowKeys with selectedVideos whenever selectedVideos or videos change
  useEffect(() => {
    // Only show checkboxes as checked for videos that are in the current search results
    const availableSelectedIds = selectedVideos
      .filter(video => videos.some(v => v.id === video.id))
      .map(v => v.id);
    setSelectedRowKeys(availableSelectedIds);
  }, [selectedVideos, videos]);

  const searchVideos = async (
    page = 1, 
    pageSize = 10, 
    searchOverride?: string,
    languageOverride?: string,
    durationOverride?: string,
    distributionPlatformOverride?: string[]
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const searchValue = searchOverride !== undefined ? searchOverride : searchText;
      const languageValue = languageOverride !== undefined ? languageOverride : languageFilter;
      const durationValue = durationOverride !== undefined ? durationOverride : durationFilter;
      const distributionPlatformValue = distributionPlatformOverride !== undefined ? distributionPlatformOverride : distributionPlatformFilter;
      
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/videos/search`,
        params: { 
          q: searchValue || undefined,
          language: languageValue !== 'all' ? languageValue : undefined,
          duration: durationValue !== 'all' ? durationValue : undefined,
          distributionPlatformIds: distributionPlatformValue.length > 0 ? distributionPlatformValue.join(',') : undefined,
          limit: pageSize,
          offset
        }
      });
      // Keys are normalised to lowercase on the server (videos/search)
      const videosList = (response.data.videos || []).map((video: any) => {
        const validCaptions = video.captions || [];
        return {
          ...video,
          selected_caption_ids: validCaptions.map((cap: any) => cap.id),
          selected_captions: validCaptions
        };
      });
      setVideos(videosList);
      setPagination({
        current: page,
        pageSize,
        total: response.data.total || 0
      });
      
      // Fetch transcoding status for these videos
      if (videosList.length > 0) {
        fetchTranscodeStatus(videosList.map((v: any) => v.id));
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOpen = () => {
    // selectedRowKeys will be synced via useEffect when modal opens
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };


  const handleSearch = (value: string) => {
    setSearchText(value);
    // Reset to first page and trigger search with new value
    searchVideos(1, pagination.pageSize, value);
  };

  const handleLanguageChange = (value: string) => {
    setLanguageFilter(value);
    // Reset to first page and trigger search with new value
    searchVideos(1, pagination.pageSize, undefined, value);
  };

  const handleDurationChange = (value: string) => {
    setDurationFilter(value);
    // Reset to first page and trigger search with new value
    searchVideos(1, pagination.pageSize, undefined, undefined, value);
  };

  const handleDistributionPlatformChange = (values: string[]) => {
    setDistributionPlatformFilter(values);
    // Reset to first page and trigger search with new value
    searchVideos(1, pagination.pageSize, undefined, undefined, undefined, values);
  };

  const handleTableChange = (newPagination: any) => {
    searchVideos(newPagination.current, newPagination.pageSize);
  };

  const handleRefresh = () => {
    searchVideos(pagination.current, pagination.pageSize);
  };

  const fetchTranscodeStatus = async (videoIds: string[]) => {
    if (videoIds.length === 0) return;
    
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/videos/transcode-status`,
        params: {
          videoIds: videoIds.join(',')
        }
      });
      
      setTranscodeStatusMap(response.data.statusMap || {});
    } catch (error) {
      console.error('Error fetching transcoding status:', error);
      // Don't show error to user, just log it
    }
  };

  const handleYoutubeReadyFilterChange = (value: string) => {
    setYoutubeReadyFilter(value);
    // Filter is applied client-side, so we don't need to refetch
  };

  const isYoutubeReady = (videoId: string): boolean => {
    return transcodeStatusMap[videoId]?.status === 'COMPLETE';
  };

  const handleInfoClick = async (video: Video, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedVideoForMetadata(video);
    setMetadataModalVisible(true);
    setMetadataLoading(true);
    setMetadata(null);

    try {
      // First, try to get existing metadata
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/videos/${video.id}/metadata`,
      });

      if (response.data.metadata) {
        // Metadata exists, display it
        setMetadata(response.data.metadata);
      } else {
        // Metadata doesn't exist, analyze the video
        message.info('Metadata not found. Analyzing video...');
        const analyzeResponse = await authAxios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/videos/${video.id}/analyze`,
        });

        if (analyzeResponse.data.metadata) {
          setMetadata(analyzeResponse.data.metadata);
          message.success('Video analyzed successfully');
        } else {
          message.error('Failed to analyze video');
        }
      }
    } catch (error: any) {
      console.error('Error fetching/analyzing metadata:', error);
      message.error(error?.response?.data?.error || 'Failed to load metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleMetadataModalClose = () => {
    setMetadataModalVisible(false);
    setSelectedVideoForMetadata(null);
    setMetadata(null);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys.map(key => `available-${key}`),
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: Video[]) => {
      // Remove the "available-" prefix from keys
      const cleanKeys = (newSelectedRowKeys as string[]).map(key => key.replace('available-', ''));
      setSelectedRowKeys(cleanKeys);
      
      // Determine which videos were added vs removed
      const previousSelectedIds = new Set(selectedRowKeys);
      const newSelectedIds = new Set(cleanKeys);
      
      // Find videos that were added (checked)
      const addedVideoIds = cleanKeys.filter(id => !previousSelectedIds.has(id));
      // Find videos that were removed (unchecked)
      const removedVideoIds = selectedRowKeys.filter(id => !newSelectedIds.has(id));
      
      // Get full video objects for added videos from current search results
      const addedVideos = selectedRows.filter(v => addedVideoIds.includes(v.id));
      
      // Create a map of existing selected videos for merging
      const existingVideosMap = new Map(selectedVideos.map(v => [v.id, v]));
      
      // Update existing videos with new caption selections, and keep videos not in current results unchanged
      const updatedVideos = selectedVideos.map(video => {
        const newVideo = addedVideos.find(v => v.id === video.id);
        if (newVideo) {
          // Update caption selection for existing video with full caption data
          return { 
            ...video, 
            captions: newVideo.captions, 
            selected_caption_ids: newVideo.selected_caption_ids,
            selected_captions: newVideo.selected_captions
          };
        }
        return video;
      });
      
      // Add new videos (not already in selectedVideos)
      const existingVideoIds = new Set(selectedVideos.map(v => v.id));
      const trulyNewVideos = addedVideos.filter(video => !existingVideoIds.has(video.id));
      
      // Remove videos that were unchecked
      const finalVideos = [...updatedVideos, ...trulyNewVideos].filter(
        video => !removedVideoIds.includes(video.id)
      );
      
      // Update parent component
      onVideosSelected(finalVideos);
      
      // Show feedback messages
      if (trulyNewVideos.length > 0 && removedVideoIds.length === 0) {
        message.success(`Added ${trulyNewVideos.length} video(s) to stream`);
      } else if (removedVideoIds.length > 0 && trulyNewVideos.length === 0) {
        message.success(`Removed ${removedVideoIds.length} video(s) from stream`);
      } else if (trulyNewVideos.length > 0 && removedVideoIds.length > 0) {
        message.success(`Added ${trulyNewVideos.length} and removed ${removedVideoIds.length} video(s)`);
      } else if (addedVideoIds.length > 0 && existingVideoIds.size > 0) {
        // Updated existing videos (caption changes)
        const updatedCount = addedVideoIds.filter(id => existingVideoIds.has(id)).length;
        if (updatedCount > 0) {
          message.success(`Updated ${updatedCount} video(s)`);
        }
      }
    },
    getCheckboxProps: (record: Video) => ({
      // Allow unchecking already-selected videos to remove them
    }),
  };

  const videoColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '40%',
      ellipsis: false,
      render: (text: string, record: Video) => (
        <div style={{ maxWidth: '100%' }}>
          <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{text}</div>
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            maxWidth: '100%'
          }}>
            <span style={{ 
              wordBreak: 'break-word',
              flex: '1 1 auto',
              minWidth: 0
            }}>
              {record.ref_id} • {record.file_name}
            </span>
            <Tooltip title="View metadata">
              <span
                onClick={(e) => handleInfoClick(record, e)}
                className="video-info-icon"
                style={{ 
                  cursor: 'pointer',
                  color: '#1890ff',
                  fontSize: '14px',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 2px',
                  visibility: 'visible',
                  opacity: 1,
                  position: 'relative',
                  zIndex: 10,
                  lineHeight: 1
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  target.style.color = '#40a9ff';
                  target.style.opacity = '1';
                  target.style.visibility = 'visible';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  target.style.color = '#1890ff';
                  target.style.opacity = '1';
                  target.style.visibility = 'visible';
                }}
              >
                <InfoCircleOutlined style={{ display: 'block' }} />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (language: string) => (
        <Tag color="blue">{language}</Tag>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number) => formatDuration(duration),
      sorter: (a: Video, b: Video) => a.duration - b.duration,
    },
    {
      title: 'Captions',
      key: 'captions',
      width: 200,
      render: (text: any, record: Video) => {
        const handleCaptionToggle = (captionId: string, e: any) => {
          e.stopPropagation();
          const currentSelected = record.selected_caption_ids || [];
          const newSelected = currentSelected.includes(captionId)
            ? currentSelected.filter(id => id !== captionId)
            : [...currentSelected, captionId];
          
          // Also get the full caption objects for the selected IDs
          const selectedCaptionObjects = (record.captions || []).filter(cap => 
            newSelected.includes(cap.id)
          );
          
          // Update the videos state with both IDs and full objects
          setVideos(prevVideos =>
            prevVideos.map(v =>
              v.id === record.id
                ? { ...v, selected_caption_ids: newSelected, selected_captions: selectedCaptionObjects }
                : v
            )
          );
          
          // If this video is already in selectedVideos, update it there too
          const isSelected = selectedVideos.some(v => v.id === record.id);
          if (isSelected) {
            const updatedSelectedVideos = selectedVideos.map(v =>
              v.id === record.id
                ? { ...v, selected_caption_ids: newSelected, selected_captions: selectedCaptionObjects }
                : v
            );
            onVideosSelected(updatedSelectedVideos);
          }
        };
        
        if (record.captions && record.captions.length > 0) {
          return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {record.captions.map((caption) => {
                const isSelected = (record.selected_caption_ids || []).includes(caption.id);
                return (
                  <div key={caption.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleCaptionToggle(caption.id, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Tooltip 
                      title={
                        <div>
                          <div><strong>ID:</strong> {caption.id}</div>
                          <div><strong>URL:</strong> {caption.url}</div>
                          <div><strong>Type:</strong> {caption.caption_type?.toUpperCase() ?? 'N/A'}</div>
                        </div>
                      } 
                      placement="topLeft"
                    >
                      <Tag 
                        color={isSelected ? "green" : "default"} 
                        style={{ margin: 0, cursor: 'pointer' }}
                      >
                        {caption.language} ({caption.caption_type?.toUpperCase() ?? 'N/A'})
                      </Tag>
                    </Tooltip>
                  </div>
                );
              })}
            </Space>
          );
        }
        
        // Legacy: Check for single caption_url
        if (record.caption_url) {
          return (
            <Space>
              <Tag color="green">
                <a 
                  href={record.caption_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'inherit' }}
                >
                  CC
                </a>
              </Tag>
              {record.caption_type && (
                <Tag color="default">{record.caption_type.toUpperCase()}</Tag>
              )}
            </Space>
          );
        }
        
        return (
          <Tag color="orange" icon={<WarningOutlined />}>
            No Captions
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      key: 'youtube_ready',
      width: 120,
      render: (text: any, record: Video) => {
        if (isYoutubeReady(record.id)) {
          return (
            <Tag color="success" icon={<PlayCircleOutlined />}>
              Youtube Ready
            </Tag>
          );
        }
        return null;
      },
    }
  ];

  return (
    <div>
      <Button 
        type="primary" 
        icon={<PlusOutlined />}
        onClick={handleModalOpen}
        style={{ marginBottom: '16px' }}
      >
        Add Videos
      </Button>

      <Modal
        title="Select Videos"
        open={showModal}
        onCancel={handleModalClose}
        width={1000}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancel
          </Button>,
        ]}
      >
        <Row gutter={16} style={{ marginBottom: '8px' }}>
          <Col span={6}>
            <Search
              placeholder="Search videos..."
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by language"
              value={languageFilter}
              onChange={handleLanguageChange}
              style={{ width: '100%' }}
            >
              <Option value="all">All Languages</Option>
              <Option value="English">English</Option>
              <Option value="Spanish">Spanish</Option>
              <Option value="French">French</Option>
              {/* Add more languages as needed */}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by duration"
              value={durationFilter}
              onChange={handleDurationChange}
              style={{ width: '100%' }}
            >
              <Option value="all">All Durations</Option>
              <Option value="short">Short (&lt; 5 min)</Option>
              <Option value="medium">Medium (5-30 min)</Option>
              <Option value="long">Long (&gt; 30 min)</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Youtube Ready"
              value={youtubeReadyFilter}
              onChange={handleYoutubeReadyFilterChange}
              style={{ width: '100%' }}
            >
              <Option value="all">All Videos</Option>
              <Option value="ready">Youtube Ready Only</Option>
              <Option value="not-ready">Not Ready</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Button onClick={handleRefresh} style={{ width: '100%' }}>
              Refresh
            </Button>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Select
              mode="multiple"
              placeholder="Filter by distribution platform"
              value={distributionPlatformFilter}
              onChange={handleDistributionPlatformChange}
              style={{ width: '100%' }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {distributionPlatformData?.map((platform: any) => (
                <Option key={platform.id} value={platform.id}>
                  {platform.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          columns={videoColumns}
          dataSource={videos.filter(video => {
            // Apply Youtube Ready filter
            if (youtubeReadyFilter === 'ready') {
              return isYoutubeReady(video.id);
            } else if (youtubeReadyFilter === 'not-ready') {
              return !isYoutubeReady(video.id);
            }
            return true; // 'all' - show all videos
          })}
          rowSelection={rowSelection}
          rowKey={(record) => `available-${record.id}`}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} videos`
          }}
          onChange={handleTableChange}
          scroll={{ y: 400 }}
        />
      </Modal>

      <Modal
        title={`Video Metadata - ${selectedVideoForMetadata?.title || ''}`}
        open={metadataModalVisible}
        onCancel={handleMetadataModalClose}
        width={800}
        footer={[
          <Button key="close" onClick={handleMetadataModalClose}>
            Close
          </Button>,
        ]}
      >
        {metadataLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading metadata...</div>
          </div>
        ) : metadata ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Video Codec">{metadata.video_codec || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Resolution">
              {metadata.video_width && metadata.video_height 
                ? `${metadata.video_width}x${metadata.video_height}` 
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Duration (seconds)">{metadata.video_duration_seconds || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Video Bitrate">{metadata.video_bitrate ? `${metadata.video_bitrate} bps` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Frame Rate">{metadata.video_fps ? `${metadata.video_fps} fps` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Pixel Format">{metadata.video_pixel_format || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Aspect Ratio">{metadata.video_aspect_ratio || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Audio Codec">{metadata.audio_codec || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Audio Sample Rate">{metadata.audio_sample_rate ? `${metadata.audio_sample_rate} Hz` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Audio Channels">{metadata.audio_channels || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Audio Bitrate">{metadata.audio_bitrate ? `${metadata.audio_bitrate} bps` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Audio Language">{metadata.audio_language || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Caption Count">{metadata.caption_count || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Caption Languages">{metadata.caption_languages || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="File Format">{metadata.file_format || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="File Size">{metadata.file_size_bytes ? `${(metadata.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Container Format">{metadata.container_format || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Analyzed At">
              {metadata.analyzed_at ? new Date(metadata.analyzed_at).toLocaleString() : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            No metadata available
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VideoSelector;
